import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OnboardingLayout from './OnboardingLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { QrCode, Package, CheckCircle, Loader2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const OnboardingQRTest = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [delivery, setDelivery] = useState(null);

  const createDemoDelivery = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/onboarding/demo/delivery`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDelivery(response.data);
      toast.success('Demo delivery created!');
    } catch (error) {
      console.error('Create demo error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create demo delivery');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    navigate('/onboarding/done');
  };

  return (
    <OnboardingLayout currentStep={4}>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <QrCode className="h-6 w-6" />
            QR Code Test
          </h2>
          <p className="text-gray-500 mt-1">
            Test your driver app with a demo delivery
          </p>
        </div>

        {!delivery ? (
          <Card className="p-8 text-center space-y-4">
            <Package className="h-16 w-16 mx-auto text-gray-400" />
            <div>
              <h3 className="font-semibold text-lg">Create a Demo Delivery</h3>
              <p className="text-sm text-gray-600 mt-2">
                We'll create a test delivery that your drivers can scan and claim
              </p>
            </div>
            <Button onClick={createDemoDelivery} disabled={loading} size="lg">
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Demo Delivery'
              )}
            </Button>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* QR Code Display */}
            <Card className="p-8">
              <div className="text-center space-y-4">
                <h3 className="font-semibold text-lg">Demo Delivery QR Code</h3>
                <div className="flex justify-center">
                  <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                    <QRCodeSVG value={delivery.qr_data} size={200} />
                  </div>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>Delivery ID: <span className="font-mono font-medium">{delivery.delivery_id}</span></p>
                  <p>Customer: {delivery.delivery?.customer_name}</p>
                  <p>Address: {delivery.delivery?.delivery_address}</p>
                </div>
              </div>
            </Card>

            {/* Instructions */}
            <Card className="p-6 bg-blue-50 border-blue-200">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-blue-600" />
                How to Test
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-sm">
                <li>Open the Flotto Driver App on your mobile device</li>
                <li>Login with one of your driver accounts</li>
                <li>Tap "Scan QR Code" or use the QR Scanner</li>
                <li>Scan this QR code to claim the delivery</li>
                <li>Test the delivery workflow (navigation, contact, proof of delivery)</li>
              </ol>
            </Card>

            <Button onClick={() => createDemoDelivery()} variant="outline" className="w-full">
              Create Another Demo Delivery
            </Button>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-6 border-t">
          <Button variant="outline" onClick={() => navigate('/onboarding/cashx')}>
            Back
          </Button>
          <Button onClick={handleComplete}>
            Complete Setup
          </Button>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default OnboardingQRTest;
